import React from "react";
import apiClient, { formatApiErrorDetail } from "@/lib/api";
import { Mail, Send, Loader2, AlertCircle, CheckCircle2, Users } from "lucide-react";

const SEGMENT_LABELS = {
  waitlist: "Waitlist signups",
  customers: "Verified customers",
  drivers_verified: "Verified drivers",
  drivers_all: "All drivers (incl. unverified)",
};

export default function AdminBroadcastPanel() {
  const [segments, setSegments] = React.useState({});
  const [templates, setTemplates] = React.useState([]);
  const [history, setHistory] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [sending, setSending] = React.useState(false);
  const [success, setSuccess] = React.useState("");

  const [form, setForm] = React.useState({
    segment: "waitlist",
    subject: "",
    html_body: "",
    template_id: "",
  });

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const [segs, tmpls, list] = await Promise.all([
        apiClient.get("/admin/broadcasts/segments"),
        apiClient.get("/admin/broadcasts/templates"),
        apiClient.get("/admin/broadcasts"),
      ]);
      setSegments(segs.data.segments || {});
      setTemplates(tmpls.data.templates || []);
      setHistory(list.data.broadcasts || []);
      setError("");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load broadcasts");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const applyTemplate = (id) => {
    const t = templates.find((x) => x.id === id);
    if (!t) return;
    setForm((f) => ({ ...f, template_id: id, subject: t.subject || "", html_body: t.html_body || "" }));
  };

  const submit = async (e) => {
    e.preventDefault();
    const count = segments[form.segment];
    if (count && count > 0 && !window.confirm(`Send this broadcast to ${count} recipients in segment "${SEGMENT_LABELS[form.segment]}"? This cannot be undone.`)) {
      return;
    }
    setSending(true);
    setError("");
    setSuccess("");
    try {
      const { data } = await apiClient.post("/admin/broadcasts", {
        segment: form.segment,
        subject: form.subject.trim(),
        html_body: form.html_body,
        template_id: form.template_id || null,
      });
      setSuccess(`Queued to ${data.recipient_count} recipients. Sending in background…`);
      setForm({ segment: form.segment, subject: "", html_body: "", template_id: "" });
      setTimeout(load, 1500);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="mb-12" data-testid="admin-broadcast-panel">
      <div className="flex items-end justify-between gap-3 mb-4 flex-wrap">
        <div>
          <h2 className="font-display text-2xl font-bold tracking-tight text-[#0A0A0A] inline-flex items-center gap-2">
            <Mail size={20} /> Marketing broadcasts
          </h2>
          <p className="mt-1 text-sm text-[#475569]">Send templated or custom emails to your waitlist, customers, or drivers.</p>
        </div>
      </div>

      {error && (
        <div className="mb-3 flex items-start gap-2 text-sm text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] p-3" data-testid="broadcast-error">
          <AlertCircle size={16} className="mt-0.5 shrink-0" /><span>{error}</span>
        </div>
      )}
      {success && (
        <div className="mb-3 flex items-start gap-2 text-sm text-[#2c8642] bg-[#5BB247]/10 border border-[#5BB247]/40 p-3" data-testid="broadcast-success">
          <CheckCircle2 size={16} className="mt-0.5 shrink-0" /><span>{success}</span>
        </div>
      )}

      {loading ? (
        <p className="text-sm text-[#475569] inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</p>
      ) : (
        <div className="grid lg:grid-cols-3 gap-6">
          <form onSubmit={submit} className="lg:col-span-2 bg-white border border-[#0A0A0A]/10 p-6 space-y-4" data-testid="broadcast-form">
            <div>
              <label className="block text-xs uppercase tracking-[0.18em] font-bold text-[#0A0A0A] mb-2">Recipients</label>
              <select
                value={form.segment}
                onChange={(e) => setForm((f) => ({ ...f, segment: e.target.value }))}
                data-testid="broadcast-segment"
                className="w-full px-3 py-2.5 border border-[#0A0A0A]/15 text-sm bg-white"
              >
                {Object.entries(SEGMENT_LABELS).map(([k, label]) => (
                  <option key={k} value={k}>{label} ({segments[k] ?? "?"})</option>
                ))}
              </select>
            </div>

            {templates.length > 0 && (
              <div>
                <label className="block text-xs uppercase tracking-[0.18em] font-bold text-[#0A0A0A] mb-2">Start from template (optional)</label>
                <select
                  value={form.template_id}
                  onChange={(e) => applyTemplate(e.target.value)}
                  data-testid="broadcast-template"
                  className="w-full px-3 py-2.5 border border-[#0A0A0A]/15 text-sm bg-white"
                >
                  <option value="">— Custom email —</option>
                  {templates.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                </select>
              </div>
            )}

            <div>
              <label className="block text-xs uppercase tracking-[0.18em] font-bold text-[#0A0A0A] mb-2">Subject</label>
              <input
                required
                value={form.subject}
                onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
                placeholder="A short, punchy subject line"
                data-testid="broadcast-subject"
                className="w-full px-3 py-2.5 border border-[#0A0A0A]/15 text-sm"
              />
            </div>

            <div>
              <label className="block text-xs uppercase tracking-[0.18em] font-bold text-[#0A0A0A] mb-2">HTML body</label>
              <textarea
                required
                minLength={10}
                rows={10}
                value={form.html_body}
                onChange={(e) => setForm((f) => ({ ...f, html_body: e.target.value }))}
                placeholder="<p>Hey,</p><p>...</p>"
                data-testid="broadcast-body"
                className="w-full px-3 py-2.5 border border-[#0A0A0A]/15 text-sm font-mono"
              />
            </div>

            <button
              type="submit"
              disabled={sending}
              data-testid="broadcast-send"
              className="w-full bg-[#1E4D55] hover:bg-[#163C42] text-white px-5 py-3 font-semibold transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
            >
              {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              {sending ? "Sending…" : `Send to ${segments[form.segment] ?? "?"} recipients`}
            </button>
          </form>

          <div className="bg-white border border-[#0A0A0A]/10 p-6">
            <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#1E4D55]">Recent broadcasts</p>
            {history.length === 0 ? (
              <p className="mt-3 text-sm text-[#475569]">Nothing sent yet.</p>
            ) : (
              <div className="mt-3 space-y-3 max-h-[400px] overflow-y-auto" data-testid="broadcast-history">
                {history.map((b) => (
                  <div key={b.id} className="border border-[#0A0A0A]/10 p-3" data-testid={`broadcast-history-${b.id}`}>
                    <p className="text-xs font-semibold text-[#0A0A0A] line-clamp-1">{b.subject}</p>
                    <p className="mt-1 text-[11px] text-[#475569] inline-flex items-center gap-1.5">
                      <Users size={11} /> {SEGMENT_LABELS[b.segment] || b.segment} · {b.sent_count}/{b.recipient_count} sent
                      {b.failed_count ? ` · ${b.failed_count} failed` : ""}
                    </p>
                    <p className="mt-1 text-[10px] uppercase tracking-[0.18em] font-bold text-[#475569]">{b.status}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
